import { z } from "zod";

/**
 * Environment config — the SINGLE place `import.meta.env` is read in the whole app.
 *
 * Everything else imports the typed `config` object from `@core/config`; nothing else touches
 * `import.meta.env`. Validation runs once at module load, so a missing or malformed VITE_ var
 * fails LOUD at startup (a thrown ZodError, visible immediately) instead of surfacing as a
 * mysterious runtime bug deep in a feature later.
 *
 * Adding a future var is a one-line change, not a refactor:
 *   1. add the field to `EnvSchema` below (with its validation + default),
 *   2. surface it on the `config` object,
 *   3. declare it on `ImportMetaEnv` in `src/vite-env.d.ts` for editor typing.
 */
const EnvSchema = z.object({
  /**
   * Backend API base URL. Empty by default: the SPA talks to the same-origin relative
   * `/api/v1` path (served by Caddy) in every environment, so no absolute URL is baked into
   * any build. This slot exists for a future topology where the API lives on another origin —
   * set it in `.env.production` then and wire it into `@core/libs/axios`.
   */
  VITE_API_BASE_URL: z.string().default(""),

  // --- Future slots (uncomment the field here + its `config` line + its vite-env.d.ts entry
  //     when the infra decision lands) -----------------------------------------------------
  // Error-reporting sink DSN (Sentry / GlitchTip / …). When set, the logger's prod error-sink
  // seam (registerErrorReporter) forwards redacted entries to it.
  // VITE_ERROR_SINK_DSN: z.string().url().optional(),
});

// Fails loud at startup if a VITE_ var is malformed. `import.meta.env` also carries Vite's
// built-ins (DEV/PROD/MODE/…); Zod strips those unknown keys, leaving only our validated vars.
const env = EnvSchema.parse(import.meta.env);

/**
 * Typed, validated app config. The semantic build flags wrap Vite's built-ins so the rest of
 * the codebase never reads `import.meta.env` directly.
 */
export const config = {
  /** True under `vite dev` (any non-production build). */
  isDev: import.meta.env.DEV,
  /** True in a production build (`vite build`). */
  isProd: import.meta.env.PROD,
  /** Raw Vite mode ("development" | "production" | a custom `--mode`). */
  mode: import.meta.env.MODE,
  /** See VITE_API_BASE_URL above. "" ⇒ same-origin relative `/api/v1`. */
  apiBaseUrl: env.VITE_API_BASE_URL,
} as const;

export type AppConfig = typeof config;
